import {
  Button,
  InputContainer,
  InputFile,
  InputLabel,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { useState } from "react";
import { Alert } from "~/components/alert/Alert";
import type { RyftRequiredDocument } from "../onboarding/components/RyftNonHostedOnboardingProvider.shared";
import {
  ACCEPTED_DOCUMENT_FILES,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPES_REQUIRING_BACK,
  MAXIMUM_DOCUMENT_FILE_SIZE,
  MINIMUM_DOCUMENT_FILE_SIZE,
  SUPPORTED_DOCUMENT_FILE_EXTENSIONS,
} from "../onboarding/constants";

type DocumentType = RyftRequiredDocument["types"][number];

export type RyftDocumentSubmissionValues = {
  front: File;
  back?: File;
  documentType: DocumentType;
};

export const RyftEditDocumentForm = ({
  mode,
  documentTypes,
  isSubmitting,
  disabled = false,
  submissionError,
  onSubmit,
}: {
  mode: "create" | "update";
  documentTypes: DocumentType[];
  isSubmitting: boolean;
  disabled?: boolean;
  submissionError?: string;
  onSubmit: (values: RyftDocumentSubmissionValues) => void;
}) => {
  const [selectedDocumentType, setSelectedDocumentType] = useState<
    DocumentType | undefined
  >(documentTypes.length === 1 ? documentTypes[0] : undefined);
  const [frontFile, setFrontFile] = useState<File>();
  const [backFile, setBackFile] = useState<File>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const backFileIsRequired = DOCUMENT_TYPES_REQUIRING_BACK.some(
    (documentType) => documentType === selectedDocumentType,
  );

  const submit = () => {
    setErrorMessage(undefined);
    if (!selectedDocumentType) {
      setErrorMessage("Select a document type.");
      return;
    }
    if (!frontFile) {
      setErrorMessage("Choose the front of the document to upload.");
      return;
    }
    if (backFileIsRequired && !backFile) {
      setErrorMessage("Choose the back of the document to upload.");
      return;
    }

    const frontValidationError = validateFile(frontFile);
    if (frontValidationError) {
      setErrorMessage(`Front: ${frontValidationError}`);
      return;
    }

    const backValidationError = backFile ? validateFile(backFile) : undefined;
    if (backValidationError) {
      setErrorMessage(`Back: ${backValidationError}`);
      return;
    }

    if (disabled || isSubmitting) return;
    onSubmit({
      front: frontFile,
      back: backFile,
      documentType: selectedDocumentType,
    });
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        submit();
      }}
    >
      <fieldset
        className="flex flex-col gap-4 disabled:opacity-60"
        disabled={disabled || isSubmitting}
      >
        {(errorMessage || submissionError) && (
          <Alert
            type="error"
            title="Unable to upload document"
            message={
              errorMessage || submissionError || "Unable to upload document"
            }
            className="w-full"
          />
        )}

        <div className="flex flex-col gap-1">
          <InputLabel>Document type</InputLabel>
          <InputContainer>
            <InputSelect
              value={selectedDocumentType ?? ""}
              disabled={isSubmitting}
              onChange={(event) => {
                setSelectedDocumentType(event.target.value as DocumentType);
                setBackFile(undefined);
                setErrorMessage(undefined);
              }}
            >
              <InputOption value="">Select a document type</InputOption>
              {documentTypes.map((documentType) => (
                <InputOption key={documentType} value={documentType}>
                  {DOCUMENT_TYPE_LABELS[documentType]}
                </InputOption>
              ))}
            </InputSelect>
          </InputContainer>
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel>Front</InputLabel>
          <InputContainer>
            <InputFile
              accept={ACCEPTED_DOCUMENT_FILES}
              disabled={isSubmitting}
              onChange={(event) => {
                setFrontFile(event.target.files?.[0]);
                setErrorMessage(undefined);
              }}
            />
          </InputContainer>
        </div>

        {backFileIsRequired && (
          <div className="flex flex-col gap-1">
            <InputLabel>Back</InputLabel>
            <InputContainer>
              <InputFile
                accept={ACCEPTED_DOCUMENT_FILES}
                disabled={isSubmitting}
                onChange={(event) => {
                  setBackFile(event.target.files?.[0]);
                  setErrorMessage(undefined);
                }}
              />
            </InputContainer>
          </div>
        )}
        <div className="flex justify-end">
          <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
            {mode === "create" ? "Upload" : "Replace document"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};

const validateFile = (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase();

  if (
    !extension ||
    !SUPPORTED_DOCUMENT_FILE_EXTENSIONS.includes(
      extension as (typeof SUPPORTED_DOCUMENT_FILE_EXTENSIONS)[number],
    )
  ) {
    return "Upload a PDF, JPEG or PNG file.";
  }

  if (file.size < MINIMUM_DOCUMENT_FILE_SIZE) {
    return "The file must be at least 10KB.";
  }

  if (file.size > MAXIMUM_DOCUMENT_FILE_SIZE) {
    return "The file must not exceed 4MB.";
  }

  return undefined;
};
